// Scripted content for the chat-demo mode of the guided intake (see
// demoFlags). With the flag on, the upload buttons attach these documents
// instead of opening the file picker, and each click of the chat send button
// plays the next exchange: no text is typed or dictated, and nothing is sent
// to the AI backend. The story is a $173M AWS Private Pricing Addendum; the
// numbers match the demo analysis report, so the same script sets up any deal
// the presenter then discusses with the assistant.

/** A fake attachment: its row copy, plus the pacing of its fake extraction. */
export interface DemoDocument {
    filename: string;
    size: string;
    /** Document types shown on the row's pills once "extracted". Used as a
     *  fallback when the kind's requirement groups don't provide a type. */
    types: string[];
    /** How long the row shows the parsing spinner before turning Ready. */
    parseMs: number;
}

export const DEMO_DOCUMENTS: DemoDocument[] = [
    {
        filename: "AWS - Private Pricing Addendum (Order Form).pdf",
        size: "186 KB",
        types: ["Order form / quote"],
        parseMs: 2200,
    },
    {
        filename: "AWS Customer Agreement (executed) - Demo Customer.pdf",
        size: "412 KB",
        types: ["MSA"],
        parseMs: 3600,
    },
];

/** How long the whole-set read-back "thinks" after the last document lands. */
export const DEMO_ACK_MS = 1800;

export const DEMO_SUGGESTED_NAME = "AWS Private Pricing Addendum";

/** The whole-set read-back shown under the drop area once both docs are read. */
export const DEMO_UPLOAD_READ_BACK =
    "Done reading both documents. This is a three-year AWS Private Pricing Addendum " +
    "committing $173M of cloud spend ($50M, $58M, $65M, a ~14% year-over-year ramp), " +
    "running January 2026 through December 2028. Headline terms: a 19% cross-service " +
    "discount, EC2 Instance Savings Plans at 25% and Compute Savings Plans at 22%, a " +
    "Marketplace cap at 25% of commitment, and $7M of conditional credits ($4M AI/ML, " +
    "$3M migration). Two things I'd flag already: termination-for-convenience is " +
    "suspended during the PPA, and egress pricing has no negotiated protection. " +
    "I've suggested a project name. Add anything else, or continue.";

/** The agent's opener when the presenter picks "Talk it through". */
export const DEMO_CONVERSATION_OPENER =
    "Thanks, I've read the order form and the customer agreement, so I have the " +
    "commercial picture. Tell me about your commercial objectives: what you want out " +
    "of this negotiation, what you're willing to trade, and how hard you want to push.";

/** One scripted chat turn: the "user" answer and the agent's reply. Each send
 *  click plays the next one and marks another context topic covered. */
export interface DemoExchange {
    user: string;
    reply: string;
    /** Typing-dots delay before the reply lands. */
    replyMs: number;
}

export const DEMO_EXCHANGES: DemoExchange[] = [
    {
        user:
            "This is our first formal Private Pricing Addendum with AWS. We've been on " +
            "consumption pricing until now. The big commercial goals are getting Savings Plan " +
            "depth closer to market (they're offering 22% on Compute, we should be in the " +
            "high twenties or thirties at our commitment size) and lifting the Marketplace cap " +
            "from 25% to something like 50%. Posture is firm but not aggressive: we want to " +
            "close, but we'll trade a longer term and a stronger upfront commitment for ramp " +
            "flexibility and better discount depth.",
        reply:
            "Got it. Savings Plan depth and the Marketplace cap are your top economic levers, " +
            "posture is firm, and you'll trade term length for ramp flexibility. " +
            "Next, what are the contractual risks you want the analyst to scrutinize?",
        replyMs: 1600,
    },
    {
        user:
            "On the paper side, my top concerns are termination rights and the ramp. The PPA " +
            "strips termination-for-convenience, which we've had in every prior framework " +
            "agreement, and the ~14% year-over-year ramp doesn't reflect our actual demand " +
            "curve; regulatory approvals gate how fast we can actually grow. Egress is also a " +
            "real exposure: no negotiated step-down, so moving workloads gets penalized. " +
            "Standard AWS Customer Agreement IP and liability terms are fine, no need to fight those.",
        reply:
            "Noted. Termination, ramp shape, and egress are the exposures to work; IP and " +
            "liability stay as-is. How would you describe the relationship with AWS: " +
            "performance, history, past negotiations?",
        replyMs: 1600,
    },
    {
        user:
            "The account team has been strong. Our AE is consultative and the solutions " +
            "architects have been embedded with our platform group for eighteen months. Where " +
            "AWS gets rigid is the PPA desk itself: discretionary on which levers they'll move, " +
            "and they slow-walk anything touching headline price. We've won credit conversions " +
            "in past rounds but nothing structural on commit shape. Technical relationship is " +
            "genuinely good; commercial is where the friction sits.",
        reply:
            "Got the relationship picture: technical side strong, PPA desk rigid on structural " +
            "commit shape. Last topic: what are your market alternatives? What's your BATNA " +
            "going into Round 2?",
        replyMs: 1600,
    },
    {
        user:
            "We're running parallel proposals from Azure and GCP, not because we want to " +
            "migrate, but because we need credible BATNA for Round 2. Switching is realistic " +
            "for new workloads, much harder for the existing platform footprint given how deep " +
            "the AWS-native dependencies run. Azure is the stronger commercial alternative; GCP " +
            "is more credible on AI and analytics. Treat the alternatives as leverage, not as a " +
            "real path.",
        reply:
            "Got the BATNA picture: Azure and GCP as leverage rather than a migration plan. " +
            "Combined with the documents and the context you've given, the analyst has " +
            "everything needed. Hit “Request Analysis” when you're ready.",
        replyMs: 1600,
    },
];
